import React from 'react'
import { Home, Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../../ui/dialog'
import { Button } from '../../../ui/button'
import { Label } from '../../../ui/label'
import { Input } from '../../../ui/input'
import {
  mappingBaseUrlToSocialIcon,
  socialMediaBaseUrl,
} from '../../../../core/utils/media-platform.utils'
import { SocialMediaInfluencer } from '../../../../core/interface/chronique.interface'

interface InfluencerSocialMediaModalProps {
  action: 'add' | 'edit'
  selectedSocialMedia?: SocialMediaInfluencer | null
  showSocialModal: boolean
  setShowSocialModal: (show: boolean) => void
  handleCreateSocialMedia?: (url: string, followers: number) => void
  handleUpdateSocialMedia?: (
    social_media_id: string,
    url: string,
    followers: number,
  ) => void
  handleDeleteSocialMedia?: (social_media_id: string) => void
}

const InfluencerSocialMediaModal: React.FC<InfluencerSocialMediaModalProps> = ({
  action,
  selectedSocialMedia,
  showSocialModal,
  setShowSocialModal,
  handleCreateSocialMedia,
  handleUpdateSocialMedia,
  handleDeleteSocialMedia,
}) => {
  const { t } = useTranslation()

  const [url, setUrl] = React.useState<string>('')
  const [followers, setFollowers] = React.useState<number>(0)
  const [Icon, setIcon] = React.useState<React.ElementType>(Home)

  React.useEffect(() => {
    if (selectedSocialMedia) {
      setUrl(selectedSocialMedia.url)
      setFollowers(selectedSocialMedia.followers)
    } else {
      setUrl('')
      setFollowers(0)
    }
  }, [selectedSocialMedia])

  React.useEffect(() => {
    const baseUrls: string[] = Object.values(socialMediaBaseUrl)
    const matchedBaseUrl = baseUrls.find((baseUrl) => url.startsWith(baseUrl))
    if (matchedBaseUrl) {
      const IconComponent =
        mappingBaseUrlToSocialIcon[
          matchedBaseUrl as keyof typeof mappingBaseUrlToSocialIcon
        ]
      setIcon(IconComponent || Home)
    } else {
      setIcon(Home)
    }
  }, [url])

  const handleValidate = () => {
    if (action === 'add' && handleCreateSocialMedia) {
      handleCreateSocialMedia(url, followers)
    } else if (
      action === 'edit' &&
      handleUpdateSocialMedia &&
      selectedSocialMedia
    ) {
      handleUpdateSocialMedia(selectedSocialMedia.id.toString(), url, followers)
    }
  }

  return (
    <Dialog open={showSocialModal} onOpenChange={setShowSocialModal}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {action === 'edit'
              ? t('chroniqueurView.socialMedia.editSocialMedia')
              : t('chroniqueurView.socialMedia.addSocialMedia')}
          </DialogTitle>
        </DialogHeader>

        <div className="flex items-center gap-2">
          <Icon className="h-5 w-5" />
          <Label className="text-sm">
            {t(
              followers > 1
                ? 'chroniqueurView.follows_other'
                : 'chroniqueurView.follows_one',
              { count: followers },
            )}
          </Label>
        </div>

        <div>
          <Label className="text-sm font-medium">
            {t('chroniqueurView.socialMedia.socialMediaUrl')}
          </Label>
          <Input
            className="mt-1"
            placeholder={t('chroniqueurView.socialMedia.urlPlaceholders')}
            value={url}
            onChange={(e) => setUrl(e.target.value)}
          />
        </div>

        <div>
          <Label className="text-sm font-medium">
            {t('chroniqueurView.socialMedia.followersCount')}
          </Label>
          <Input
            className="mt-1"
            placeholder={t('chroniqueurView.socialMedia.followerPlaceholders')}
            type="number"
            value={followers}
            onChange={(e) => setFollowers(Number(e.target.value))}
          />
        </div>

        <div className="flex justify-end gap-2 pt-4">
          {action === 'edit' && (
            <Button
              variant="outline"
              onClick={() =>
                handleDeleteSocialMedia?.(
                  selectedSocialMedia?.id?.toString() || '',
                )
              }
            >
              <Trash2 className="h-4 w-4 mr-1" />
            </Button>
          )}
          <Button onClick={() => handleValidate()}>
            {t('commons.buttons.validate')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default InfluencerSocialMediaModal
